import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyAccess } from "../../services/access.service";
import { formatDate } from "../../utils/date";
import LoadTrigger from "../../components/common/LoadTrigger";
import PageHeader from "../../components/common/PageHeader";
import "../../styles/user/myAccess.css";

const TYPE_LABELS = { animation: "Animation", publication: "Visualization", folder: "Folder" };
const FILTERS = ["all", "animation", "publication", "folder"];

const MyAccess = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState("all");
  const pageRef = useRef(1);
  const reqIdRef = useRef(1);
  const syncRef = useRef(false);

  const onLoadMore = async (reset = false) => {
    if (!reset && (syncRef.current || !hasMore)) return;
    setIsLoading(true);
    syncRef.current = true;
    try {
      const query = {
        page: reset ? 1 : pageRef.current,
        ...(filter !== "all" ? { contentType: filter } : {}),
      };
      if (reset) { pageRef.current = 2; ++reqIdRef.current; setHasMore(true); }
      else pageRef.current++;
      const reqId = reqIdRef.current;
      const data = await getMyAccess(query);
      if (reqId !== reqIdRef.current) return;
      setHasMore(data.page < data.totalPages);
      setRecords((prev) => reset ? data.records : [...prev, ...data.records]);
    } catch (err) {
      --pageRef.current;
    } finally {
      setIsLoading(false);
      syncRef.current = false;
    }
  };

  useEffect(() => {
    const t = setTimeout(() => onLoadMore(true), 100);
    return () => clearTimeout(t);
  }, [filter]);

  return (
    <>
      <PageHeader
        title="My Access"
        description="Content you have purchased or been granted access to."
      />

      <div className="my-access-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`my-access-filter-btn ${filter === f ? "active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "All" : TYPE_LABELS[f]}
          </button>
        ))}
      </div>

      <div className="my-access-list">
        {records.map((r, i) => (
          <div key={i} className="my-access-item">
            <div className="my-access-item-left">
              <span className="my-access-type">{TYPE_LABELS[r.contentType] || r.contentType}</span>
              <span className="my-access-id">{r.contentId}</span>
            </div>
            <div className="my-access-item-right">
              <span className={`my-access-model ${r.pricingModel}`}>{r.pricingModel}</span>
              <span className="my-access-date">
                {r.expiresAt
                  ? `Expires ${formatDate(r.expiresAt)}`
                  : "Lifetime"}
              </span>
            </div>
          </div>
        ))}

        {!isLoading && records.length === 0 && (
          <div className="my-access-empty">
            <p>No access records found.</p>
            <button className="my-access-browse-btn" onClick={() => navigate("/f/visualizations")}>
              Browse Content
            </button>
          </div>
        )}
      </div>

      <LoadTrigger hasMore={hasMore} isLoading={isLoading} onLoadMore={onLoadMore} />
    </>
  );
};

export default MyAccess;
